import { useState } from 'react';
import useSWR from 'swr';
import { adminApi } from '../../lib/api';
import type { AdminAuditEvent } from '../../types';

const PAGE_SIZE = 100;

export function AuditPage() {
  const [filters, setFilters] = useState<{
    actor?: string;
    actionPrefix?: string;
    targetType?: string;
    since?: string;
    until?: string;
  }>({});
  const [skip, setSkip] = useState(0);

  const { data } = useSWR(
    ['admin/audit', filters, skip],
    () => adminApi.listAudit({ ...filters, limit: PAGE_SIZE, skip }),
    { keepPreviousData: true }
  );

  const set = (k: string, v: string | undefined) => {
    setFilters((f) => ({ ...f, [k]: v || undefined }));
    setSkip(0);
  };

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-gray-100">Audit log</h1>

      <div className="bg-gray-900 border border-gray-800 rounded p-4 grid grid-cols-5 gap-3 text-sm">
        <FilterField label="Actor (email contains)">
          <input value={filters.actor || ''} onChange={(e) => set('actor', e.target.value)} className="w-full bg-gray-950 border border-gray-800 rounded px-2 py-1 text-gray-100" />
        </FilterField>
        <FilterField label="Action prefix">
          <select value={filters.actionPrefix || ''} onChange={(e) => set('actionPrefix', e.target.value)} className="w-full bg-gray-950 border border-gray-800 rounded px-2 py-1 text-gray-100">
            <option value="">any</option>
            <option value="auth.">auth.*</option>
            <option value="user.">user.*</option>
            <option value="org.">org.*</option>
            <option value="apikey.">apikey.*</option>
          </select>
        </FilterField>
        <FilterField label="Target type">
          <select value={filters.targetType || ''} onChange={(e) => set('targetType', e.target.value)} className="w-full bg-gray-950 border border-gray-800 rounded px-2 py-1 text-gray-100">
            <option value="">any</option>
            <option value="User">User</option>
            <option value="Organization">Organization</option>
            <option value="Membership">Membership</option>
            <option value="ApiKey">ApiKey</option>
          </select>
        </FilterField>
        <FilterField label="Since">
          <input type="datetime-local" value={filters.since || ''} onChange={(e) => set('since', e.target.value)} className="w-full bg-gray-950 border border-gray-800 rounded px-2 py-1 text-gray-100" />
        </FilterField>
        <FilterField label="Until">
          <input type="datetime-local" value={filters.until || ''} onChange={(e) => set('until', e.target.value)} className="w-full bg-gray-950 border border-gray-800 rounded px-2 py-1 text-gray-100" />
        </FilterField>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-3 py-2 font-medium">When</th>
              <th className="px-3 py-2 font-medium">Actor</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Target</th>
              <th className="px-3 py-2 font-medium">Metadata</th>
              <th className="px-3 py-2 font-medium">IP</th>
            </tr>
          </thead>
          <tbody>
            {data?.events.map((e: AdminAuditEvent) => (
              <tr key={e._id} className="border-b border-gray-800/60 align-top">
                <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">{new Date(e.createdAt).toLocaleString()}</td>
                <td className="px-3 py-2 text-gray-300 text-xs">{e.actorEmail || '—'}</td>
                <td className="px-3 py-2"><code className="text-xs text-blue-300">{e.action}</code></td>
                <td className="px-3 py-2 text-gray-400 text-xs">
                  {e.targetType ? `${e.targetType}` : '—'}
                  {e.targetId && <div className="text-gray-600">{String(e.targetId).slice(-8)}</div>}
                </td>
                <td className="px-3 py-2 text-gray-400 text-xs max-w-md">
                  {e.metadata && Object.keys(e.metadata).length > 0 && (
                    <code className="block whitespace-pre-wrap break-all">{JSON.stringify(e.metadata, null, 0)}</code>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-500 text-xs">{e.ip || '—'}</td>
              </tr>
            ))}
            {data && data.events.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No matching events</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} className="px-3 py-1 rounded bg-gray-800 disabled:opacity-30">Prev</button>
            <button disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} className="px-3 py-1 rounded bg-gray-800 disabled:opacity-30">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
